import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, User, Mail, Phone, Car, Clock, Building2,
  Send, Zap, CheckCircle, Timer, X as XIcon, Eye, AlertTriangle, Hash, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatSignature } from '../utils/signatureFormatter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function cleanEmailBody(raw) {
  if (!raw) return '';
  let text = raw;
  // Remove everything after <<End Message:>> or << End Message: >>
  text = text.replace(/<<?\s*End\s*Message\s*:?\s*>>?.*/s, '');
  // Strip HTML tags (img, input, a, div, span, br, etc.)
  text = text.replace(/<[^>]+>/g, '');
  // Remove <<Pager Message:>> wrapper markers
  text = text.replace(/<<?\s*Pager\s*Message\s*:?\s*>>/gi, '');
  // Collapse excessive blank lines (3+ newlines → 2)
  text = text.replace(/\n{3,}/g, '\n\n');
  return text.trim();
}

function formatResponseTime(seconds) {
  if (!seconds) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

export default function ResponseDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [clearingVinCache, setClearingVinCache] = useState(false);

  const clearVinCache = async (vin) => {
    if (!vin) return;
    setClearingVinCache(true);
    try {
      await axios.delete(`${API}/admin/vdp-cache/${vin}`, { headers });
      toast.success(`VDP cache cleared for ${vin}`);
    } catch {
      toast.error('Failed to clear VDP cache');
    } finally {
      setClearingVinCache(false);
    }
  };

  const handlePreviewResponse = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(
        `${API}/leads/${leadId}/preview-response`,
        {},
        { headers, withCredentials: true, timeout: 60000 }
      );
      setPreview(res.data.preview);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
    setPreviewing(false);
  };

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const res = await axios.get(`${API}/leads/${leadId}`, { headers, withCredentials: true });
        setLead(res.data);
      } catch { navigate('/responses'); }
      setLoading(false);
    };
    fetchLead();
  }, [leadId]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(`${API}/leads/${leadId}/approve`, {}, { headers, withCredentials: true });
      setLead(prev => ({ ...prev, response_status: 'sent', sent_at: new Date().toISOString() }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve response');
    }
    setApproving(false);
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API}/leads/${leadId}/reject`, {}, { headers, withCredentials: true });
      setLead(prev => ({ ...prev, response_status: 'rejected' }));
    } catch (err) { console.error('Failed to reject lead:', err); toast.error('Failed to reject'); }
  };

  const [resending, setResending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const handleResendViaCrm = async () => {
    if (!window.confirm('Resend this response through the VinSolutions CRM browser? The customer will receive an email (unless VINSOLUTIONS_DRY_RUN=true on the server).')) return;
    setResending(true);
    try {
      const res = await axios.post(
        `${API}/vinsolutions/resend/${leadId}`,
        {},
        { headers, withCredentials: true, timeout: 180000 }
      );
      if (res.data.success) {
        toast.success('Resent via CRM — check VinSolutions lead history.');
        setLead(prev => ({ ...prev, response_status: 'sent_via_crm', sent_at: new Date().toISOString() }));
      } else {
        toast.error(res.data.error || 'CRM send failed — check server logs.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'CRM resend failed');
    }
    setResending(false);
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Re-run the pipeline and overwrite this lead\'s stored response? Useful after a template update or code fix. Does not send.')) return;
    setRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/leads/${leadId}/regenerate-response`,
        {},
        { headers, withCredentials: true, timeout: 120000 }
      );
      toast.success(`Regenerated with template: ${res.data.updated?.template_name || 'unknown'}`);
      // Refetch the lead to show the new response
      const fresh = await axios.get(`${API}/leads/${leadId}`, { headers, withCredentials: true });
      setLead(fresh.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Regenerate failed');
    }
    setRegenerating(false);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lead) return null;

  const InfoItem = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        <Icon className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <div>
          <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">{label}</p>
          <p className="text-sm text-white mt-0.5">{value}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="response-detail-page">
      <button
        onClick={() => navigate('/responses')}
        className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-6"
        data-testid="back-to-responses"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Response Log
      </button>

      {/* Header with timing */}
      <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight">
              {lead.customer_name || 'Unknown'}
            </h1>
            <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${
              lead.response_status === 'sent' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
              lead.response_status === 'pending_review' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
              lead.response_status === 'no_template' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
              'bg-slate-500/10 text-slate-400 border-slate-500/20'
            }`}>
              {lead.response_status === 'pending_review' ? 'Pending Review' :
               lead.response_status === 'no_template' ? 'No Template' :
               lead.response_status}
            </Badge>
          </div>
          <p className="text-slate-400 text-sm mt-1">{lead.dealership_name} &middot; {lead.lead_type_label}</p>
        </div>
        {/* Response time highlight */}
        <Card className="bg-[#0A0A0A] border-[#262626] p-4 flex items-center gap-4">
          <Timer className="w-5 h-5 text-red-500" />
          <div>
            <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Response Time</p>
            <p className="font-heading text-2xl font-bold text-white">{formatResponseTime(lead.response_time_seconds)}</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Incoming lead */}
        <div className="lg:col-span-5 space-y-6">
          {/* Timing */}
          <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="timing-info">
            <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-red-500" /> Timing
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-[#1A1A1A]">
                <span className="text-xs text-slate-500 font-bold tracking-wider uppercase">Received</span>
                <span className="text-sm text-white font-mono">{lead.received_at ? new Date(lead.received_at).toLocaleString() : '--'}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[#1A1A1A]">
                <span className="text-xs text-slate-500 font-bold tracking-wider uppercase">Responded</span>
                <span className="text-sm text-white font-mono">{lead.responded_at ? new Date(lead.responded_at).toLocaleString() : '--'}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-xs text-slate-500 font-bold tracking-wider uppercase">Response Time</span>
                <span className="text-sm text-red-400 font-heading font-bold">{formatResponseTime(lead.response_time_seconds)}</span>
              </div>
            </div>
          </Card>

          {/* Customer info */}
          <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="customer-info">
            <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-4">
              Customer Information
            </h3>
            <div className="space-y-1 divide-y divide-[#1A1A1A]">
              <InfoItem icon={User} label="Name" value={lead.customer_name} />
              <InfoItem icon={Mail} label="Email" value={lead.customer_email || lead.sender_email} />
              <InfoItem icon={Phone} label="Phone" value={lead.customer_phone} />
              <InfoItem icon={Building2} label="Dealership" value={lead.dealership_name} />
              {lead.vin && (
                <div className="flex items-start justify-between py-2">
                  <div className="flex items-start gap-3">
                    <Hash className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                      <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">VIN Number</p>
                      <p className="text-sm text-white mt-0.5 font-mono">{lead.vin}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => clearVinCache(lead.vin)}
                    disabled={clearingVinCache}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] tracking-widest uppercase h-7 px-3"
                    data-testid="clear-vin-cache-btn"
                  >
                    {clearingVinCache ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Clear VIN'}
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Vehicle info */}
          <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="vehicle-info">
            <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-4">
              Vehicle Details
            </h3>
            <div className="space-y-1 divide-y divide-[#1A1A1A]">
              <InfoItem icon={Car} label="Vehicle" value={
                [lead.vehicle_year, lead.vehicle_make, lead.vehicle_model, lead.vehicle_trim].filter(Boolean).join(' ') || null
              } />
              <InfoItem icon={Car} label="Color" value={lead.vehicle_color} />
              <InfoItem icon={Car} label="Trade-In" value={lead.trade_in_vehicle} />
            </div>
          </Card>

          {/* Original email */}
          <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="original-email">
            <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-4">
              Incoming Email
            </h3>
            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-4 overflow-hidden">
              <p className="text-xs text-slate-500 mb-1">From: {lead.sender_email}</p>
              <p className="text-xs text-slate-500 mb-3">Subject: {lead.email_subject}</p>
              <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{cleanEmailBody(lead.email_body)}</p>
            </div>
          </Card>
        </div>

        {/* Right: Auto-generated response */}
        <div className="lg:col-span-7 space-y-6">
          {/* AI Summary */}
          <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="ai-summary">
            <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-red-500" /> AI Analysis
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed">{lead.ai_summary || 'No AI summary available'}</p>
            <div className="flex items-center gap-3 mt-4">
              <Badge variant="outline" className="text-[9px] font-bold tracking-wider border-[#333] text-slate-400 uppercase">
                {lead.lead_type_label || lead.lead_type}
              </Badge>
              {lead.template_name && (
                <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[9px] font-bold tracking-wider uppercase">
                  Template: {lead.template_name}
                </Badge>
              )}
            </div>
          </Card>

          {/* Generated response */}
          <Card className={`bg-[#0A0A0A] border-[#262626] p-6 ${
            lead.response_status === 'pending_review' ? 'border-orange-600/30' : ''
          }`} data-testid="generated-response">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider">
                Auto-Generated Response
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handlePreviewResponse}
                  disabled={previewing}
                  variant="outline"
                  className="h-8 px-3 text-[10px] tracking-wider uppercase bg-transparent border-[#262626] text-slate-300 hover:bg-[#1a1a1a] hover:text-white"
                  data-testid="preview-response-btn"
                  title="Re-run the pipeline with current templates/code. Shows what would be delivered. Does not send."
                >
                  {previewing ? (
                    <div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Eye className="w-3 h-3 mr-1.5" /> Preview
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  variant="outline"
                  className="h-8 px-3 text-[10px] tracking-wider uppercase bg-transparent border-blue-500/40 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
                  data-testid="regenerate-response-btn"
                  title="Re-run the pipeline AND save the new response back to this lead. Useful after template edits or code fixes. Does not send."
                >
                  {regenerating ? (
                    <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Zap className="w-3 h-3 mr-1.5" /> Regenerate
                    </>
                  )}
                </Button>
                {lead.crm_type === 'vinsolutions' && lead.response_body && (
                  <Button
                    onClick={handleResendViaCrm}
                    disabled={resending}
                    variant="outline"
                    className="h-8 px-3 text-[10px] tracking-wider uppercase bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    data-testid="resend-crm-btn"
                    title="Re-paste this response into the VinSolutions CRM composer and send."
                  >
                    {resending ? (
                      <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-3 h-3 mr-1.5" /> Resend via CRM
                      </>
                    )}
                  </Button>
                )}
                <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${
                  lead.response_status === 'sent' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                  lead.response_status === 'pending_review' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                  'bg-red-500/10 text-red-400 border-red-500/20'
                }`}>
                  {lead.response_status === 'pending_review' ? 'Pending Review' : lead.response_status}
                </Badge>
              </div>
            </div>

            {lead.response_body ? (
              <>
                <div className="bg-white rounded-lg p-6 mb-4">
                  <div className="mb-3 pb-3 border-b border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">To: {lead.sender_email}</p>
                    <p className="text-xs text-gray-500">Subject: {lead.response_subject}</p>
                  </div>
                  {lead.logo_url && !lead.response_body.includes('<img') && (
                    <div className="text-center mb-4">
                      <img src={lead.logo_url} alt="Logo" className="max-h-20 inline-block" onError={(e) => { e.target.style.display = 'none'; }} data-testid="response-logo" />
                    </div>
                  )}
                  <div 
                    className="template-preview text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none [&>p]:mb-3 [&>ul]:mb-3 [&>ul]:ml-5 [&>ul>li]:mb-1 [&>p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatSignature(lead.response_body)) }}
                    data-testid="response-body-rendered"
                  />
                </div>

                {/* Approve/Reject for pending */}
                {lead.response_status === 'pending_review' && (
                  <div className="flex gap-3">
                    <Button
                      onClick={handleApprove}
                      disabled={approving}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold text-xs tracking-widest uppercase h-11 hover:shadow-[0_0_15px_rgba(16,185,129,0.4)] transition-all"
                      data-testid="approve-response-btn"
                    >
                      {approving ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <><CheckCircle className="w-4 h-4 mr-2" /> Approve & Send</>
                      )}
                    </Button>
                    <Button
                      onClick={handleReject}
                      variant="outline"
                      className="border-[#333] text-slate-300 hover:bg-red-500/10 hover:text-red-400 hover:border-red-600/30 font-bold text-xs tracking-widest uppercase h-11 transition-all"
                      data-testid="reject-response-btn"
                    >
                      <XIcon className="w-4 h-4 mr-2" /> Reject
                    </Button>
                  </div>
                )}

                {lead.response_status === 'sent' && (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-bold text-xs tracking-wider uppercase">Response sent successfully</span>
                    {lead.sent_at && (
                      <span className="text-xs text-slate-500 ml-2">{new Date(lead.sent_at).toLocaleString()}</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 text-center">
                <Zap className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                <p className="text-slate-500 text-sm">No matching template found for this lead type.</p>
                <p className="text-slate-600 text-xs mt-1 mb-4">Add a template for "{lead.lead_type_label}" to this dealership.</p>
                <Button
                  onClick={handlePreviewResponse}
                  disabled={previewing}
                  variant="outline"
                  className="h-8 px-3 text-[10px] tracking-wider uppercase bg-transparent border-[#262626] text-slate-300 hover:bg-[#1a1a1a] hover:text-white"
                  data-testid="preview-response-btn-no-template"
                >
                  {previewing ? (
                    <div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Eye className="w-3 h-3 mr-1.5" /> Re-run Pipeline Preview</>
                  )}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Preview Response Modal */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="preview-modal-backdrop"
        >
          <div
            className="bg-[#0A0A0A] border border-[#262626] rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
            data-testid="preview-modal"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#262626]">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-blue-400" />
                <h3 className="font-heading text-base font-semibold text-white uppercase tracking-wider">
                  Pipeline Preview — not sent
                </h3>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="text-slate-400 hover:text-white"
                data-testid="preview-modal-close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {/* Metadata grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-4 border-b border-[#262626] text-xs">
                <div>
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Template</div>
                  <div className="text-white" data-testid="preview-template-name">
                    {preview.template_name || <span className="text-orange-400">— none matched —</span>}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Lead Type</div>
                  <div className="text-white">{preview.lead_type_label || preview.lead_type || '—'}</div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Matched Alias</div>
                  <div className="text-white break-all">{preview.matched_alias_email || '—'}</div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">VIN Detected</div>
                  <div className="text-white font-mono">{preview.vin || '—'}</div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Vehicle</div>
                  <div className="text-white">
                    {[preview.vehicle_year, preview.vehicle_make, preview.vehicle_model, preview.vehicle_trim]
                      .filter(Boolean).join(' ') || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">VDP Found</div>
                  <div className={preview.vehicle_found_on_website ? 'text-green-400' : 'text-orange-400'}>
                    {preview.vehicle_found_on_website ? 'yes' : 'no'}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">CRM</div>
                  <div className="text-white">{preview.crm_type || '—'}</div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Status</div>
                  <div className="text-white">{preview.response_status || preview.skipped_reason || '—'}</div>
                </div>
              </div>

              {/* Fallback indicators */}
              {(preview.vin_not_found_fallback || preview.repo_not_found_fallback) && (
                <div className="px-6 py-3 bg-orange-500/5 border-b border-orange-500/20 text-xs">
                  <div className="flex items-center gap-2 text-orange-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span>
                      Fallback triggered:
                      {preview.vin_not_found_fallback && ' VIN not found on website.'}
                      {preview.repo_not_found_fallback && ' Vehicle not in repository.'}
                      {' '}Template was swapped to General Inquiry.
                    </span>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div className="px-6 py-3 border-b border-[#262626] text-xs">
                <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Subject</div>
                <div className="text-white" data-testid="preview-subject">{preview.response_subject || '—'}</div>
              </div>

              {/* Body */}
              <div className="p-6 bg-white m-6 rounded-lg">
                {preview.response_body ? (
                  <div
                    className="text-sm text-gray-800 leading-relaxed [&>p]:mb-3 [&>ul]:mb-3 [&>ul]:ml-5 [&>ul>li]:mb-1 [&_img]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:max-w-full"
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.response_body) }}
                    data-testid="preview-body"
                  />
                ) : (
                  <div className="text-center text-slate-500 py-12">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-orange-400" />
                    <p className="text-sm">
                      No response body was produced.
                      {preview.skipped_reason ? ` Reason: ${preview.skipped_reason}` : ' Check template matching above.'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-[#262626] flex justify-end">
              <Button
                onClick={() => setPreview(null)}
                className="bg-[#262626] hover:bg-[#363636] text-white text-xs tracking-wider uppercase h-9"
                data-testid="preview-modal-close-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
